import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Dices, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { HoldTypeDialog } from '../climbs/climb-editor/HoldTypeDialog';
import type { ClimbScanColor } from '../../types/climb-scan';

export interface ColorFormState {
  climbName: string;
  gradeId: string;
  colorName: string;
}

interface ColorStatusBadgeProps { status: ClimbScanColor['status'] }

function ColorStatusBadge({ status }: ColorStatusBadgeProps) {
  const { t } = useTranslation();
  const colors: Record<ClimbScanColor['status'], string> = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    draft: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status]}`}>
      {t(`climbScan.colorStatus.${status}`)}
    </span>
  );
}

interface Props {
  color: ClimbScanColor;
  grades: Array<{ id: string; name: string }>;
  gymSlug: string;
  scanClimbType: 'boulder' | 'route';
  isExpanded: boolean;
  formState: ColorFormState;
  fieldErrors: Record<string, string>;
  isSaving: boolean;
  isDeleting: boolean;
  onToggleExpand: () => void;
  onFormChange: (patch: Partial<ColorFormState>) => void;
  onDrawSuggestion?: () => void;
  onSaveDraft: (andNavigate: boolean) => void;
  onDelete: () => void;
  onNavigateToClimb: () => void;
}

export function ColorRow({
  color,
  grades,
  gymSlug,
  isExpanded,
  formState,
  fieldErrors,
  isSaving,
  isDeleting,
  onToggleExpand,
  onFormChange,
  onDrawSuggestion,
  onSaveDraft,
  onDelete,
  onNavigateToClimb,
}: Props) {
  const { t } = useTranslation();
  const isEditable = color.status !== 'completed';

  return (
    <Card className={color.status === 'completed' ? 'opacity-60' : ''}>
      <CardContent className="p-0">
        {/* Header */}
        <div
          role="button"
          tabIndex={isEditable ? 0 : -1}
          aria-disabled={!isEditable}
          aria-expanded={isExpanded}
          className={`w-full p-4 flex items-center justify-between gap-3 text-left hover:bg-muted/30 transition-colors ${
            isEditable ? 'cursor-pointer' : 'cursor-not-allowed'
          }`}
          onClick={() => isEditable && onToggleExpand()}
          onKeyDown={(e) => {
            if (!isEditable) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleExpand();
            }
          }}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex gap-1 flex-shrink-0">
              {color.hex_colors.slice(0, 3).map((hex, i) => (
                <div
                  key={i}
                  className="w-5 h-5 rounded-full border border-border"
                  style={{ backgroundColor: hex }}
                />
              ))}
              {color.hex_colors.length === 0 && (
                <div className="w-5 h-5 rounded-full border border-dashed border-border" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {color.color_name || t('climbScan.unnamedColor')}
              </p>
              {color.hold_count != null && (
                <p className="text-xs text-muted-foreground">
                  {t('climbScan.holdCount', { count: color.hold_count })}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <ColorStatusBadge status={color.status} />
            {color.climb && (
              <button
                className="text-xs text-primary underline hover:no-underline"
                onClick={e => { e.stopPropagation(); onNavigateToClimb(); }}
              >
                {color.climb.name}
              </button>
            )}
            {isEditable && (
              <button
                className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                onClick={e => { e.stopPropagation(); onDelete(); }}
                disabled={isDeleting}
                title={t('climbScan.color.cancel')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {isEditable && (isExpanded
              ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
              : <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Inline form */}
        {isExpanded && isEditable && (
          <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
            {/* The gym's own list rather than free text — and a colour it has
                never used is added to that list on the spot, so the next one on
                this wall is offered it. */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('climbScan.color.colorName')}
              </label>
              <HoldTypeDialog
                as="field"
                value={formState.colorName}
                locationId={gymSlug}
                onChange={(colorName) => onFormChange({ colorName })}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('climbScan.color.climbName')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className={`flex-1 min-w-0 px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring ${fieldErrors.name ? 'border-destructive' : 'border-input'}`}
                  value={formState.climbName}
                  onChange={e => onFormChange({ climbName: e.target.value })}
                  placeholder={t('climbScan.color.climbNamePlaceholder')}
                />
                {onDrawSuggestion && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="flex-shrink-0"
                    onClick={onDrawSuggestion}
                    title={t('climbScan.color.drawName')}
                    aria-label={t('climbScan.color.drawName')}
                  >
                    <Dices className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {fieldErrors.name && (
                <p className="text-xs text-destructive">{fieldErrors.name}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('climbScan.color.grade')}
              </label>
              <select
                className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                value={formState.gradeId}
                onChange={e => onFormChange({ gradeId: e.target.value })}
              >
                <option value="">{t('climbCreate.selectGrade')}</option>
                {grades.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            {Object.entries(fieldErrors).filter(([k]) => k !== 'name').map(([, msg]) => (
              <p key={msg} className="text-xs text-destructive">{msg}</p>
            ))}

            <div className="flex gap-2 pt-1 flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={onDelete}
                disabled={isDeleting || isSaving}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {t('climbScan.color.cancel')}
              </Button>
              <div className="flex-1" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSaveDraft(false)}
                disabled={isSaving || isDeleting}
              >
                {isSaving ? t('common.loading') : t('climbScan.color.save')}
              </Button>
              <Button
                size="sm"
                onClick={() => onSaveDraft(true)}
                disabled={isSaving || isDeleting}
              >
                {t('climbScan.color.saveAndInsert')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
